import { IconSearch, IconX } from "@tabler/icons-react";
import cn from "classnames";
import { Checkbox, Field } from "components/shared/form";
import Pagination from "components/shared/reactTable/pagination";
import React, { useEffect, useMemo, useState } from "react";
import {
    EditablePerformancePlan,
    PerformancePlanFilter,
    PerformancePlanSubjectModel,
    PerformancePlanValue,
} from "./types";

type PaginationProps = {
    page: number;
    pages: number;
    pageSize: number;
    pageSizeOptions: number[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    isNotExportable?: boolean;
};

const TablePagination = Pagination as unknown as React.ComponentType<PaginationProps>;

type PlanSection = "organization" | "logists" | "cars";
type SubjectSection = Exclude<PlanSection, "organization">;
type PlanField = "mileagePlan" | "overallBidsPricePlan";

type PerformancePlanSettingsProps = {
    draft: EditablePerformancePlan;
    initialDraft: EditablePerformancePlan;
    filter: PerformancePlanFilter;
    isDirty: boolean;
    isSaving: boolean;
    saveError: string | null;
    onChange: (draft: EditablePerformancePlan) => void;
    onSave: () => void;
};

const hasPlan = ({ mileagePlan, overallBidsPricePlan }: Pick<PerformancePlanSubjectModel, PlanField>) =>
    mileagePlan !== null || overallBidsPricePlan !== null;

const hasValidValue = (value: PerformancePlanValue) => value === null || (Number.isFinite(value) && value >= 0);

const planValueValidator = (value?: number | string | null) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    return Number.isFinite(Number(value)) && Number(value) >= 0 ? "" : "Введите неотрицательное число";
};

const planValueValidators = [planValueValidator];

const normalizePlanValue = (value: unknown): PerformancePlanValue => {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
};

type PlanValueFieldProps = {
    name: PlanField;
    value: PerformancePlanValue;
    subjectName: string;
    disabled: boolean;
    onChange: (field: PlanField, value: PerformancePlanValue) => void;
};

function PlanValueField({ name, value, subjectName, disabled, onChange }: PlanValueFieldProps) {
    const label = name === "mileagePlan" ? "Пробег" : "Валовая выручка";
    return (
        <Field
            type="number"
            inputProps={{
                name,
                type: "number",
                step: "0.01",
                min: 0,
                disabled,
                placeholder: "План не задан",
                "aria-label": `${label}: ${subjectName}`,
                className: "form-control form-control-xs",
            }}
            value={value}
            onChange={({ value: nextValue }: { value: unknown }) => onChange(name, normalizePlanValue(nextValue))}
            validators={planValueValidators}
            bWrapper="mb-0"
            fieldWrapper="col-12 px-0"
        />
    );
}

type SectionHeaderProps = {
    title: string;
    expanded: boolean;
    onToggle: () => void;
};

function SectionHeader({ title, expanded, onToggle }: SectionHeaderProps) {
    return (
        <button type="button" className="performance-plans__section-header" aria-expanded={expanded} onClick={onToggle}>
            <i
                className={cn("inline-icon mr-2", expanded ? "icon-uEA61-angle-down" : "icon-uEAAE-angle-right-solid")}
            />
            {title}
        </button>
    );
}

type SubjectTableProps = {
    title: string;
    searchPlaceholder: string;
    items: PerformancePlanSubjectModel[];
    emptyText: string;
    expanded: boolean;
    disabled: boolean;
    onToggle: () => void;
    onChange: (subjectId: number, field: PlanField, value: PerformancePlanValue) => void;
};

function SubjectTable({
    title,
    searchPlaceholder,
    items,
    emptyText,
    expanded,
    disabled,
    onToggle,
    onChange,
}: SubjectTableProps) {
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);

    const filteredItems = useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) {
            return items;
        }
        return items.filter((item) => item.displayName.toLowerCase().includes(trimmed));
    }, [items, query]);

    useEffect(() => {
        setPage(0);
    }, [items.length, query]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const pagedItems = useMemo(
        () => filteredItems.slice(safePage * pageSize, (safePage + 1) * pageSize),
        [filteredItems, safePage, pageSize],
    );

    const countLabel = query.trim() ? `${filteredItems.length} из ${items.length}` : `${items.length}`;

    return (
        <section className="card performance-plans__section">
            <SectionHeader title={`${title} (${countLabel})`} expanded={expanded} onToggle={onToggle} />
            {expanded && (
                <>
                    {(items.length > 0 || query) && (
                        <div className="p-2 border-bottom d-flex align-items-center">
                            <div className="performance-plans__search-wrap">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={searchPlaceholder}
                                    aria-label={searchPlaceholder}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    disabled={disabled}
                                />
                                <IconSearch size={14} className="performance-plans__search-wrap__icon text-muted" />
                                {query && (
                                    <button
                                        type="button"
                                        className="performance-plans__search-wrap__clear text-muted"
                                        onClick={() => setQuery("")}
                                        aria-label="Очистить поиск"
                                    >
                                        <IconX size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="table-responsive">
                        {filteredItems.length ? (
                            <table className="table table-sm mb-0 performance-plans__settings-table">
                                <thead>
                                    <tr>
                                        <th>Объект</th>
                                        <th>Валовая выручка</th>
                                        <th>Пробег</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedItems.map((item) => (
                                        <tr key={item.subjectId}>
                                            <td className="align-middle font-weight-semibold">{item.displayName}</td>
                                            <td>
                                                <PlanValueField
                                                    name="overallBidsPricePlan"
                                                    value={item.overallBidsPricePlan}
                                                    subjectName={item.displayName}
                                                    disabled={disabled}
                                                    onChange={(field, value) => onChange(item.subjectId, field, value)}
                                                />
                                            </td>
                                            <td>
                                                <PlanValueField
                                                    name="mileagePlan"
                                                    value={item.mileagePlan}
                                                    subjectName={item.displayName}
                                                    disabled={disabled}
                                                    onChange={(field, value) => onChange(item.subjectId, field, value)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-muted text-center py-4">
                                {query.trim() ? "Ничего не найдено" : emptyText}
                            </div>
                        )}
                    </div>
                    {filteredItems.length > 0 && (
                        <div className="border-top">
                            <TablePagination
                                key={`${query}_${pageSize}`}
                                page={safePage}
                                pages={totalPages}
                                pageSize={pageSize}
                                pageSizeOptions={[10, 25, 50, 100]}
                                onPageChange={(nextPage: number) => setPage(nextPage)}
                                onPageSizeChange={(nextPageSize: number) => {
                                    setPageSize(nextPageSize);
                                    setPage(0);
                                }}
                                isNotExportable
                            />
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

export default function PerformancePlanSettings({
    draft,
    initialDraft,
    filter,
    isDirty,
    isSaving,
    saveError,
    onChange,
    onSave,
}: PerformancePlanSettingsProps) {
    const [expanded, setExpanded] = useState<Record<PlanSection, boolean>>({
        organization: true,
        logists: true,
        cars: true,
    });
    const organizationHasPlan = initialDraft.mileagePlan !== null || initialDraft.overallBidsPricePlan !== null;
    const showOrganization = filter === "withPlan" ? organizationHasPlan : !organizationHasPlan;
    const visibleLogistIds = new Set(
        initialDraft.logistsPlans
            .filter((item) => (filter === "withPlan" ? hasPlan(item) : !hasPlan(item)))
            .map((item) => item.subjectId),
    );
    const visibleCarIds = new Set(
        initialDraft.carsPlans
            .filter((item) => (filter === "withPlan" ? hasPlan(item) : !hasPlan(item)))
            .map((item) => item.subjectId),
    );
    const filteredLogists = draft.logistsPlans.filter((item) => visibleLogistIds.has(item.subjectId));
    const filteredCars = draft.carsPlans.filter((item) => visibleCarIds.has(item.subjectId));
    const isValid =
        hasValidValue(draft.mileagePlan) &&
        hasValidValue(draft.overallBidsPricePlan) &&
        draft.logistsPlans.every(
            (item) => hasValidValue(item.mileagePlan) && hasValidValue(item.overallBidsPricePlan),
        ) &&
        draft.carsPlans.every((item) => hasValidValue(item.mileagePlan) && hasValidValue(item.overallBidsPricePlan));
    const emptyText = filter === "withPlan" ? "Нет объектов с планом" : "Нет объектов без плана";

    const toggleSection = (section: PlanSection) =>
        setExpanded((current) => ({ ...current, [section]: !current[section] }));

    const updateOrganization = (field: PlanField, value: PerformancePlanValue) =>
        onChange({ ...draft, [field]: value });

    const updateSubject = (section: SubjectSection, subjectId: number, field: PlanField, value: PerformancePlanValue) =>
        onChange({
            ...draft,
            [`${section}Plans`]: draft[`${section}Plans`].map((item) =>
                item.subjectId === subjectId ? { ...item, [field]: value } : item,
            ),
        });

    return (
        <div className="performance-plans__settings">
            <div className="card card-body mb-3">
                <Checkbox
                    label="Переносить план на следующие месяцы"
                    inputProps={{ name: "autoCopyToNextMonth" }}
                    value={draft.autoCopyToNextMonth}
                    onChange={({ value }: { value: boolean }) => onChange({ ...draft, autoCopyToNextMonth: value })}
                    disabled={isSaving}
                    fieldBeforeLabel
                    labelWrapper="col-auto col-form-label col-form-label-sm pl-0 mb-0"
                    fieldWrapper="col-auto"
                    bWrapper="row flex-nowrap align-items-center mb-0"
                    checkboxWrapCname="my-0"
                />
                <small className="text-muted mt-2">
                    Если для следующего месяца не будет отдельной настройки, система использует этот план.
                </small>
            </div>

            <section className="card performance-plans__section">
                <SectionHeader
                    title="Компания"
                    expanded={expanded.organization}
                    onToggle={() => toggleSection("organization")}
                />
                {expanded.organization &&
                    (showOrganization ? (
                        <div className="table-responsive">
                            <table className="table table-sm mb-0 performance-plans__settings-table">
                                <thead>
                                    <tr>
                                        <th>Объект</th>
                                        <th>Валовая выручка</th>
                                        <th>Пробег</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="align-middle font-weight-semibold">Компания</td>
                                        <td>
                                            <PlanValueField
                                                name="overallBidsPricePlan"
                                                value={draft.overallBidsPricePlan}
                                                subjectName="Компания"
                                                disabled={isSaving}
                                                onChange={updateOrganization}
                                            />
                                        </td>
                                        <td>
                                            <PlanValueField
                                                name="mileagePlan"
                                                value={draft.mileagePlan}
                                                subjectName="Компания"
                                                disabled={isSaving}
                                                onChange={updateOrganization}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{emptyText}</div>
                    ))}
            </section>

            <SubjectTable
                title="Логисты"
                searchPlaceholder="Поиск по имени логиста"
                items={filteredLogists}
                emptyText={emptyText}
                expanded={expanded.logists}
                disabled={isSaving}
                onToggle={() => toggleSection("logists")}
                onChange={(subjectId, field, value) => updateSubject("logists", subjectId, field, value)}
            />
            <SubjectTable
                title="Транспортные средства"
                searchPlaceholder="Поиск по номеру машины"
                items={filteredCars}
                emptyText={emptyText}
                expanded={expanded.cars}
                disabled={isSaving}
                onToggle={() => toggleSection("cars")}
                onChange={(subjectId, field, value) => updateSubject("cars", subjectId, field, value)}
            />

            {saveError && (
                <div className="alert alert-danger mb-3" role="alert">
                    {saveError}
                </div>
            )}
            {isDirty && !isValid && (
                <div className="alert alert-danger mb-3" role="alert">
                    Плановые значения должны быть неотрицательными числами.
                </div>
            )}
            {isDirty && (
                <div className="d-flex justify-content-end pb-3">
                    <button
                        type="button"
                        className={cn("btn btn-sm btn-brand", { loading: isSaving })}
                        disabled={!isValid || isSaving}
                        onClick={onSave}
                    >
                        Сохранить
                    </button>
                </div>
            )}
        </div>
    );
}
